import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import Button from '@components/atoms/ui-kit/button';
import {
  useLanguageAndConfig,
  type LanguageConfiguration
} from '@utils/api/back-office/language-and-config/use-get-languages-and-config';
import { Plus, Edit, Check, X } from 'lucide-react';
import { PATHS } from '../../../router/paths';

/* eslint-disable no-restricted-syntax -- Semantic status pill palette */
const STATUS_PILL_ENABLED = {
  backgroundColor: 'rgba(34, 197, 94, 0.10)',
  color: '#22C55E'
};
const STATUS_PILL_DISABLED = {
  backgroundColor: 'rgba(107, 114, 128, 0.10)',
  color: '#6B7280'
};
/* eslint-enable no-restricted-syntax */

export default function LanguagesSettings() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();

  // Fetch languages data using existing hook
  const { data, isLoading, isFetching } = useLanguageAndConfig();

  const languages = data?.result || [];

  // Status badge component
  const StatusBadge = ({
    enabled,
    label
  }: {
    enabled: boolean;
    label: string;
  }) => (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={enabled ? STATUS_PILL_ENABLED : STATUS_PILL_DISABLED}
    >
      {enabled ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      {label}
    </span>
  );

  type Row = LanguageConfiguration;

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'name',
        header: t('language'),
        field: 'name',
        accessorKey: 'name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue, row }) => (
          <div>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {String(getValue() ?? '')}
            </span>
            {row.original.type === 1 && (
              <span
                className="ml-2 text-xs px-1.5 py-0.5 rounded-2xl"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('inactiveText')
                }}
              >
                {t('base')}
              </span>
            )}
          </div>
        )
      },
      {
        id: 'bcp47',
        header: t('bcp47-code'),
        field: 'bcp47',
        accessorKey: 'bcp47',
        minWidth: 120,
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span
            className="font-mono text-xs px-1.5 py-0.5 rounded-2xl"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        id: 'key',
        header: t('key'),
        field: 'key',
        accessorKey: 'key',
        minWidth: 80,
        maxWidth: 100,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span
            className="font-mono text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        id: 'is_enabled',
        header: t('status'),
        field: 'is_enabled',
        accessorKey: 'is_enabled',
        minWidth: 100,
        maxWidth: 120,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('enabled'), value: 'true' },
          { label: t('disabled'), value: 'false' }
        ],
        cell: ({ getValue }) => (
          <StatusBadge
            enabled={Boolean(getValue())}
            label={getValue() ? t('enabled') : t('disabled')}
          />
        )
      },
      {
        id: 'is_private',
        header: t('published'),
        field: 'is_private',
        accessorKey: 'is_private',
        minWidth: 100,
        maxWidth: 120,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('public'), value: 'false' },
          { label: t('private'), value: 'true' }
        ],
        cell: ({ getValue }) => (
          <StatusBadge
            enabled={!getValue()}
            label={getValue() ? t('private') : t('public')}
          />
        )
      }
    ],
    [t, getColour]
  );

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <Edit className="h-3.5 w-3.5" />,
        intent: 'neutral',
        run: (row: Row) => {
          navigate(PATHS(String(row.id)).LANGUAGE_AND_PUBLICATIONS);
        }
      }
    ],
    [t, navigate]
  );

  const handleRowClick = (row: Row) => {
    navigate(PATHS(String(row.id)).LANGUAGE_AND_PUBLICATIONS);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('languages')}
          </h3>
          <p
            className="text-xs mt-0.5"
            style={{ color: getColour('inactiveText') }}
          >
            {t('manage-language-configurations-and-models')}
          </p>
        </div>
        <Button
          label={t('add-language')}
          variant="primary"
          size="small"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => navigate(PATHS().LANGUAGE_AND_CONFIGS)}
        />
      </div>

      {/* Languages table */}
      <div
        className="rounded-2xl flex-1 min-h-0 flex flex-col"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        <DataGridV2<Row>
          data={languages}
          columns={columns}
          actions={actions}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={data?.total_results ?? languages.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 100]}
          showColumnFilters
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading || isFetching}
          onCellClick={handleRowClick}
          getRowId={row => String(row.id)}
        />
      </div>
    </div>
  );
}
